import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ItemSpec from "../../models/ItemSpec";
import { Toast } from "primereact/toast";
import { StyleService } from "../../service/StyleService";
import { Dialog } from "primereact/dialog";
import React from "react";
import { Button } from "primereact/button";
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadSelectEvent,
  ItemTemplateOptions,
} from "primereact/fileupload";
import { Tag } from "primereact/tag";

const StyleItemSpecs = () => {
  const defaultValues: ItemSpec = {
    id: 0,
    style_Cd: "",
    filename: "",
    item_Spec: "",
  };

  const toast = useRef<any>(null);
  const { styleCode } = useParams();

  const [loadingStyleItemSpecs, setLoadingStyleItemSpecs] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [styleItemSpec, setStyleItemSpec] = useState<ItemSpec>(defaultValues);
  const [styleItemSpecs, setStyleItemSpecs] = useState<ItemSpec[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!styleCode) { return; }

    StyleService.getItemSpecsByStyleCode(styleCode).then((data) => {
      setStyleItemSpecs(data);
      setLoadingStyleItemSpecs(false);
    });
  }, [refreshTrigger, styleCode]);

  const confirmDelete = (value: ItemSpec) => {
    setStyleItemSpec(value);
    setDeleteDialog(true);
  };

  const downloadItemSpec = async (value: ItemSpec) => {
    try {
      await StyleService.downloadItemSpec(value);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Exception",
        detail: "An exception occurred while adding Item Spec to style",
        life: 3000,
      });
      return null;
    }
  };

  const hideDeleteDialog = () => {
    setDeleteDialog(false);
  };

  const deleteItemSpec = () => {
    StyleService.deleteItemSpec(styleItemSpec.id).then((data: boolean) => {
      if (data) {
        setRefreshTrigger((prev) => prev + 1);
        hideDeleteDialog();
        toast.current.show({
          severity: "success",
          summary: "Successful",
          detail: "Deleted",
          life: 3000,
        });
      } else {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "Failed to remove",
          life: 3000,
        });
      }
    });
  };

  const deleteDialogFooterCE = (
    <React.Fragment>
      <Button
        label="No"
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDeleteDialog}
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        className="p-button-text"
        onClick={deleteItemSpec}
      />
    </React.Fragment>
  );

  const filenameBodyTemplate = (rowData: ItemSpec) => {
    return rowData.filename;
  };
  const rowEditorTemplate = (rowData: ItemSpec, props: any) => {
    return (
      <div>
        <Link
          className="pi pi-fw pi-download"
          title="View"
          onClick={() => downloadItemSpec(rowData)}
          to={""}
        ></Link>
        <Link
          className="pi pi-fw pi-trash rowDelete"
          title="Delete"
          onClick={() => confirmDelete(rowData)}
          to={""}
        ></Link>
      </div>
    );
  };

  const save = async (value: ItemSpec): Promise<ItemSpec | null> => {
    if (!value) {
      return null;
    }

    try {
      const data = await StyleService.addItemSpecToStyleCode(value);
      if (data) {
        setRefreshTrigger((prev) => prev + 1);
        return data;
      } else {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "Failed adding Item Spec",
          life: 3000,
        });
        return null;
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Exception",
        detail: "An exception occurred while adding Item Spec to style",
        life: 3000,
      });
      return null;
    }
  };

  function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  const handleFileSelect = (event: FileUploadSelectEvent) => {
    const files = event.files;
    var newFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      if (files[i].type === "application/pdf") {
        newFiles.push(files[i]);
      }
    }

    setUploadedFiles((uploadedFiles) => [...uploadedFiles, ...newFiles]);
  };

  const uploadItemSpec = async (event: FileUploadHandlerEvent) => {
    if (!styleCode) {
      return;
    }

    for (const file of event.files) {
      // Check if the file is a PDF by examining its MIME type
      if (file.type !== "application/pdf") {
        continue;
      }
      // Create a FileReader instance for each file
      const reader = new FileReader();

      // Create object URL for the file
      const objectURL = URL.createObjectURL(file);

      // Fetch blob data from the object URL
      const blob = await fetch(objectURL).then((r) => r.blob());

      reader.readAsArrayBuffer(blob);
      reader.onloadend = async function () {
        const base64String = arrayBufferToBase64(reader.result as ArrayBuffer);
        let _styleItemSpec = { ...styleItemSpec };
        _styleItemSpec.id = 0;
        _styleItemSpec.filename = file.name;
        _styleItemSpec.style_Cd = styleCode;
        _styleItemSpec.item_Spec = base64String;

        setLoadingStyleItemSpecs(true);
        await save(_styleItemSpec);
        setUploadedFiles([]);
        toast.current.show({ severity: 'success', summary: 'Successful', detail: 'File Uploaded', life: 3000 });
        URL.revokeObjectURL(objectURL);
      };
    }

    event.options.clear();
  };

  const onTemplateRemove = (callback: Function) => {
    callback();
  };

  const itemTemplate = (inFile: object, props: ItemTemplateOptions) => {
    const file = inFile as File;
    const validFile = file.type === "application/pdf";
    if (!validFile) {
      const errorMessage = "Invalid file type: " + file.name;
      toast.current.show({
        severity: "error",
        summary: "Exception",
        detail: errorMessage,
        life: 5000,
      });
      return;
    }
    return (
      <div className="flex align-items-center flex-wrap">
        <div className="flex align-items-center" style={{ width: "40%" }}>
          <span className="flex flex-column text-left ml-3">
            {file.name}
            <small>{new Date().toLocaleDateString()}</small>
          </span>
        </div>
        <Tag
          value={props.formatSize}
          severity="warning"
          className="px-3 py-2"
        />
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-outlined p-button-rounded p-button-danger ml-auto"
          onClick={() => onTemplateRemove(props.onRemove)}
        />
      </div>
    );
  };

  const chooseOptions = { icon: 'pi pi-fw pi-file-pdf', iconOnly: false, className: 'p-button-outlined p-button p-button-sm', label: 'Browse'};
  const uploadOptions = { icon: 'pi pi-fw pi-cloud-upload', iconOnly: false, className: 'p-button-success p-button-outlined p-button p-button-sm', label: 'Upload' };
  const cancelOptions = { icon: 'pi pi-fw pi-times', iconOnly: false, className: 'p-button-danger p-button-outlined p-button p-button-sm', label: 'Cancel' };

  return (
    <>
      <Toast ref={toast} />
      <Card className="-ml-2 mt-2">
        <div className="card">
          <FileUpload
            multiple
            accept=".pdf"
            customUpload
            uploadHandler={uploadItemSpec}
            maxFileSize={5000000}
            emptyTemplate={
              <p className="m-0">Drag and drop files to here to upload.</p>
            }
            itemTemplate={itemTemplate}
            onSelect={handleFileSelect}
            chooseOptions={chooseOptions} uploadOptions={uploadOptions} cancelOptions={cancelOptions}
          />
        </div>
        <DataTable
          value={styleItemSpecs}
          className="-ml-2"
          dataKey="id"
          size="small"
          loading={loadingStyleItemSpecs}
          emptyMessage="No cost elements found."
        >
          <Column
            field="filename"
            header="Name"
            body={filenameBodyTemplate}
            align="center"
            alignHeader="center"
          ></Column>
          <Column
            rowEditor
            body={rowEditorTemplate}
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          ></Column>
        </DataTable>
      </Card>
      <Dialog
        visible={deleteDialog}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooterCE}
        onHide={hideDeleteDialog}
      >
        <div className="confirmation-content">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: "2rem" }}
          />
          {styleItemSpec && (
            <span>
              Are you sure you want to delete <b>{styleItemSpec.filename}</b>?
            </span>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default StyleItemSpecs;
